/**
 * This component, TestCaseComparison, is responsible for rendering the comparison between the expected output and the student's output 
 * for a specific test case. It displays the input, expected output, and the differences between the expected and student's output.
 
 */
import log from "loglevel";
import React, { useEffect, useState } from "react";
//diffChars is a function from the diff package that calculates the differences between two strings.
import { diffChars } from "diff";
import "./TestCaseComparison.css"; // Import CSS for styling

const MAX_OUTPUT_LINES = 200;

const TestCaseComparison = ({ selectedTestcase }) => {

  log.log("render render")

  // State to hold the differences between the expected and student's output
  const [differences, setDifferences] = useState([]);
  const [isOutputTruncated, setIsOutputTruncated] = useState(false);

  const truncateOutput = (text, maxLines = MAX_OUTPUT_LINES) => {
      if (!text) return { text, truncated: false };
      const lines = text.split('\n');
      if (lines.length > maxLines) {
          return { text: lines.slice(0, maxLines).join('\n'), truncated: true };
      }
      return { text, truncated: false };
  };

  useEffect(() => {
    if (selectedTestcase?.testcase_output && selectedTestcase?.student_output) {
      const { text: truncatedExpected } = truncateOutput(selectedTestcase.testcase_output);
      const { text: truncatedStudent, truncated } = truncateOutput(selectedTestcase.student_output);
      setIsOutputTruncated(truncated || selectedTestcase?.output_truncated);
      setDifferences(diffChars(truncatedExpected, truncatedStudent));
    }
  }, [selectedTestcase]);

  // Function to render the differences with line numbers
  const renderDiff = () => {
    let lineNumber = 1; // Initialize line number
    let currentLine = []; // Array to hold parts of the current line
    // Iterate over the differences array
    differences.forEach((part, index) => {
      // Style for the part based on the type of difference
      const style = {
        color: "black",
        backgroundColor: part.added
          ? "#ffe6e6" // If part is added, set background color to light red
          : part.removed
          ? "#b3d9ff" // If part is removed, set background color to light blue
          : "#e6ffee", // If part is unchanged, set background color to light green
        textDecoration: part.added
          ? "line-through" // If part is added, strike through the text
          : part.removed
          ? "underline" // If part is removed, underline the text
          : "none", // If part is unchanged, no text decoration
        whiteSpace: "pre-wrap", // Preserve white spaces and line breaks
      };

      // Split the part.value by newline characters
      if (!part.value.includes("\n")) {
        // If part.value does not contain newline character
        if (!currentLine[lineNumber]) {
          // If current line is not yet initialized, initialize it as an empty array
          currentLine[lineNumber] = [];
        }
        // Push the part value to the current line
        currentLine[lineNumber].push(
          <code key={`part-${index}`} style={style}>
            {part.value}
          </code>
        );
      } else {
        // If part.value contains newline character, split it into multiple lines
        const splittedLines = part.value.split("\n");
        splittedLines.forEach((line, idx) => {
          if (idx != 0) lineNumber++; // If it's not the first line, increment the line number
          if (!currentLine[lineNumber]) {
            // If current line is not yet initialized, initialize it as an empty array
            currentLine[lineNumber] = [];
          }
          // Push the line to the current line
          currentLine[lineNumber].push(
            <code key={`part-${index}-${idx}`} style={style}>
              {line == "" ? "" : line}
            </code>
          );
        });
      }
    });

    // Render the accumulated currentLine array
    return currentLine.map((lineContent, idx) => (
      <div key={`line-${idx + 1}`} className="line-container output">
        <div className="line-number">{idx}</div>
        <div className="line-content">{lineContent}</div>
      </div>
    ));
  };

  // Function to render the expected output
  const renderOutput = () => {
    if (!selectedTestcase?.testcase_output) {
      return <>
        <span>null</span>
      </>;
    }
    const splittedLines = selectedTestcase?.testcase_output.split("\n");
    return splittedLines?.map((line, idx) => (
      <div key={`line-${idx + 1}`} className="line-container output">
        <div className="line-number">{idx + 1}</div>
        <div className="line-content">
          <code style={{ whiteSpace: "pre-wrap", color: "black" }}>{line}</code>
        </div>
      </div>
    ));
  };

  // Function to render the input
  const renderInput = () => {
    if (!selectedTestcase?.testcase_input) {
      return <span>null</span>
    }
    const splittedLines = selectedTestcase?.testcase_input.split("\n");
    return splittedLines?.map((line, idx) => (
      <div key={`line-${idx + 1}`} className="line-container output">
        <div className="line-number">{idx + 1}</div>
        <div className="line-content">
          <code style={{ whiteSpace: "pre-wrap", color: "black" }}>{line}</code>
        </div>
      </div>
    ));
  };

  return (
    <div>
      <h3>{selectedTestcase?.testcase_name}</h3>
      <hr
        style={{
          height: "1px",
          border: "none",
          backgroundColor: "#d2d2d2",
        }}
      />
      <div>
        <h5>Testcase Input</h5>
        <div className="code">{renderInput()}</div>
      </div>
      <br />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ flex: "1" }}>
          <h5>Expected Output</h5>
          <div className="code">
            <code>{renderOutput()}</code>
          </div>
        </div>
        <div style={{ flex: "1" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h5>Student Output</h5>
            <div>
              <Legend />
            </div>
          </div>
          <div className="code">{renderDiff()}</div>
          {isOutputTruncated && (
              <div style={{ padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', marginTop: '8px', fontSize: '13px', color: '#856404' }}>
                  Output truncated to {MAX_OUTPUT_LINES} lines. The full output is too large to display.
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Legend component displaying the legend for differences.

const Legend = () => (
  <table className="legend-table">
    <tbody>
      <tr style={{ height: "15px" }}>
        <td className="matched-cell" style={{ padding: "2px 4px" }}>
          Match
        </td>
        <td className="extra-cell" style={{ padding: "2px 4px" }}>
          Extra
        </td>
        <td className="remaining-cell" style={{ padding: "2px 4px" }}>
          Missing
        </td>
      </tr>
    </tbody>
  </table>
);

export default TestCaseComparison;
